import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { amazonSearchUrl } from "@/constants/amazon";
import { cn } from "@/lib/utils";

/**
 * The horizontal space one icon occupies in a keyword cell: the 16px mark plus the 8px `gap-2`
 * before it. Exported because two keyword tables size their keyword text by subtracting it, and a
 * number repeated at those call sites drifts from the component the moment the icon is resized.
 */
export const AMAZON_SEARCH_LINK_WIDTH = 16 + 8;

/** Amazon's "smile" mark, drawn in `currentColor` so it follows our tokens rather than shipping a brand asset. */
function AmazonMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      {/* lowercase "a" */}
      <path d="M14 13V8.5a2.5 2.5 0 0 0-5-0.4" />
      <path d="M14 10.5h-2.5a2.5 2.5 0 0 0 0 5c1.4 0 2.5-1 2.5-2.5" />
      <path d="M14 13c0 1.4.6 2.5 1.5 2.5" />
      {/* smile arrow */}
      <path d="M4 18c4.5 3 11 3 16-0.5" />
      <path d="M17.5 16.5 20 17.5l-.6 2.6" />
    </svg>
  );
}

/**
 * AUT-496 Part 1: the small Amazon icon that sits after a keyword and opens that keyword's
 * amazon.com search results. Ported from the design
 * (`signal-v3/src/components/shared/AmazonSearchLink.tsx`), which is where the ads team's manual
 * "open the SERP and eyeball page 1" step starts.
 *
 * **The keyword text itself stays plain text.** Making the label a link would collide with the row's
 * own click target on every table this appears in.
 *
 * Two deliberate departures from the prototype's file:
 *
 * - **No `TooltipProvider`.** The prototype mounts its own at 150 ms. Under C-31 a nested provider
 *   *resets* the delay rather than inheriting it, and the timing for the authenticated product is
 *   set once on `AppLayout`. The 150 ms intent is kept by passing `delayDuration` to this
 *   `<Tooltip>`, which is exactly what C-31(a) prescribes. A test rendering a consumer in isolation
 *   still has to mount a provider — C-31(c).
 * - **`stopPropagation` also on keydown.** Click alone leaves the keyboard path open: on a row whose
 *   handler is bound to the row element, Enter on this link would both follow the link and fire the
 *   row action.
 */
export function AmazonSearchLink({ keyword, className }: { keyword: string; className?: string }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <a
          href={amazonSearchUrl(keyword)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={`See Amazon results for "${keyword}"`}
          className={cn(
            "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <AmazonMark className="h-3.5 w-3.5" />
        </a>
      </TooltipTrigger>
      <TooltipContent>See Amazon results</TooltipContent>
    </Tooltip>
  );
}
